import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { clearEmployeeSession } from "../../../store/employeeAuthSlice";
import { changeEmployeePasswordApi } from "../api/employeeAuthApi";
import EmployeeShell from "../components/EmployeeShell";
import PasswordRules, { isStrongPassword } from "../components/PasswordRules";
import { COLABORADOR_HOME, COLABORADOR_LOGIN } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";

import "./Colaborador.css";

/**
 * Troca voluntária de senha, já dentro do portal.
 *
 * Como no primeiro acesso, a sessão é encerrada ao final: trocar a senha mata
 * todos os tokens emitidos antes, inclusive o desta aba.
 */
export default function ChangePasswordPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const goBack = useGoBack(COLABORADOR_HOME());

  const [atual, setAtual] = useState("");
  const [nova, setNova] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const confere = confirmacao.length > 0 && nova === confirmacao;
  const podeEnviar = useMemo(
    () => atual.length > 0 && isStrongPassword(nova) && confere && !loading,
    [atual, nova, confere, loading]
  );

  const trocar = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!podeEnviar) return;

    setErro(null);
    setLoading(true);

    try {
      await changeEmployeePasswordApi(atual, nova, confirmacao);

      dispatch(clearEmployeeSession());
      toast.success("Senha alterada. Entre novamente com a senha nova.");
      navigate(COLABORADOR_LOGIN(), { replace: true });
    } catch (error: any) {
      const mensagem = error?.message || "Não foi possível alterar a senha.";
      setErro(mensagem);
      toast.error(mensagem);
    } finally {
      setLoading(false);
    }
  };

  return (
    <EmployeeShell>
      <form className="colabPanel" onSubmit={trocar}>
        <h2 className="colabPanel__title">Alterar senha</h2>

        {erro ? <p className="colabError">{erro}</p> : null}

        <div className="colabField" style={{ marginBottom: 12 }}>
          <label htmlFor="colab-atual">Senha atual</label>
          <input
            id="colab-atual"
            className="colabInput"
            type="password"
            autoComplete="current-password"
            value={atual}
            onChange={(e) => setAtual(e.target.value)}
            disabled={loading}
          />
        </div>

        <div className="colabField" style={{ marginBottom: 12 }}>
          <label htmlFor="colab-nova-senha">Nova senha</label>
          <input
            id="colab-nova-senha"
            className="colabInput"
            type="password"
            autoComplete="new-password"
            value={nova}
            onChange={(e) => setNova(e.target.value)}
            disabled={loading}
          />
        </div>

        <PasswordRules value={nova} />

        <div className="colabField" style={{ margin: "12px 0" }}>
          <label htmlFor="colab-confirma-senha">Repita a nova senha</label>
          <input
            id="colab-confirma-senha"
            className="colabInput"
            type="password"
            autoComplete="new-password"
            value={confirmacao}
            onChange={(e) => setConfirmacao(e.target.value)}
            disabled={loading}
          />
          {confirmacao.length > 0 && !confere ? (
            <span className="colabHint" style={{ color: "var(--eco-danger-ink)" }}>
              As senhas não são iguais.
            </span>
          ) : null}
        </div>

        <button type="submit" className="colabBtn colabBtn--primary" disabled={!podeEnviar}>
          {loading ? "Salvando..." : "Salvar nova senha"}
        </button>

        <button
          type="button"
          className="colabBtn colabBtn--ghost"
          style={{ marginTop: 10 }}
          onClick={goBack}
          disabled={loading}
        >
          Voltar
        </button>
      </form>
    </EmployeeShell>
  );
}
